import { useEffect } from 'react';
import { APP_NAME, APPLE_TOUCH_ICON_SRC, FAVICON_PNG_SRC } from '../../../config/branding';

function resolveAbsoluteMediaUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  if (typeof window === 'undefined') return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${window.location.origin}${normalized}`;
}

/** Lock-screen / control-center artwork (PNG preferred; OS picks best size). */
export function getDefaultAudioMediaSessionArtwork() {
  const entries = [
    {
      src: resolveAbsoluteMediaUrl(APPLE_TOUCH_ICON_SRC),
      sizes: '180x180',
      type: 'image/png',
    },
    {
      src: resolveAbsoluteMediaUrl(FAVICON_PNG_SRC),
      sizes: '32x32',
      type: 'image/png',
    },
  ];
  return entries.filter((item) => item.src);
}

function lockScreenArtistLine(subtitle) {
  const text = String(subtitle || '').trim();
  if (!text) return APP_NAME;
  if (/^https?:\/\//i.test(text) || text.length > 96) return APP_NAME;
  return text;
}

/**
 * Lock-screen / control-center metadata and transport controls (where supported).
 */
export function useAudioMediaSession({
  title,
  subtitle,
  artist = '',
  artworkUrl = '',
  playing,
  audioRef,
  onTogglePlay,
  onSkipBackward,
  onSkipForward,
}) {
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession) return undefined;

    const safeTitle = String(title || 'Audio').trim() || 'Audio';
    const artistLine = String(artist || '').trim();
    const safeArtist = artistLine || lockScreenArtistLine(subtitle);
    const defaultArt = getDefaultAudioMediaSessionArtwork();
    const artwork = artworkUrl
      ? [
          { src: resolveAbsoluteMediaUrl(artworkUrl), sizes: '600x600', type: 'image/jpeg' },
          ...defaultArt,
        ]
      : defaultArt;

    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: safeTitle,
        artist: safeArtist,
        album: `${APP_NAME} Audio Player`,
        artwork,
      });
    } catch {
      /* MediaMetadata unsupported shape on some browsers */
    }

    navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';

    const setHandler = (action, fn) => {
      try {
        navigator.mediaSession.setActionHandler(action, fn);
      } catch {
        /* action not supported */
      }
    };

    setHandler('play', () => {
      onTogglePlay?.('play');
    });
    setHandler('pause', () => {
      onTogglePlay?.('pause');
    });
    setHandler('seekbackward', () => {
      onSkipBackward?.();
    });
    setHandler('seekforward', () => {
      onSkipForward?.();
    });

    return () => {
      try {
        navigator.mediaSession.playbackState = 'none';
        ['play', 'pause', 'seekbackward', 'seekforward'].forEach((action) => {
          try {
            navigator.mediaSession.setActionHandler(action, null);
          } catch {
            /* ignore */
          }
        });
      } catch {
        /* ignore */
      }
    };
  }, [title, subtitle, artist, artworkUrl, playing, onTogglePlay, onSkipBackward, onSkipForward]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession) return;
    try {
      navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
    } catch {
      /* ignore */
    }
  }, [playing]);

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio || typeof navigator === 'undefined' || !navigator.mediaSession?.setPositionState) return undefined;

    const syncPosition = () => {
      const duration = audio.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;
      try {
        navigator.mediaSession.setPositionState({
          duration,
          playbackRate: audio.playbackRate || 1,
          position: Math.min(duration, Math.max(0, audio.currentTime || 0)),
        });
      } catch {
        /* ignore */
      }
    };

    syncPosition();
    audio.addEventListener('timeupdate', syncPosition);
    audio.addEventListener('loadedmetadata', syncPosition);
    return () => {
      audio.removeEventListener('timeupdate', syncPosition);
      audio.removeEventListener('loadedmetadata', syncPosition);
    };
  }, [audioRef, title, playing]);
}
